import { App } from 'antd';
import { Dayjs } from 'dayjs';
import {
  createContext, Dispatch, ReactNode, SetStateAction,
  useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import { Duration } from 'dayjs/plugin/duration';
import { State } from 'types/app.types';
import { enhancedImageService } from 'services/enhancedImageService';
import MainAppContext from '../MainAppContext';
import ProductsContext from '../ProductsContext';
import {
  calculateRanges, calculateTime, utcDayjs,
} from '../../utils';

interface TimeSeriesProviderProps {
    children: ReactNode,
}
export type RangeValue = Dayjs[];

enum Dimension {
  Time = 'time',
  Elevation = 'elevation'
}
interface TimeSeriesContextValues {
    play: () => void,
    stop: () => void,
    frameRating: string,
    setFrameRating: Dispatch<SetStateAction<string>>,
    range: RangeValue,
    setCurrentPosition: Dispatch<SetStateAction<string>>,
    currentPosition: string,
    preloadRange: RangeValue | null,
    setPreloadRange: Dispatch<SetStateAction<RangeValue | null>>
    preloadingState: (State | null)[],
    animationState: (State | null)[],
    setPreloadingState: Dispatch<SetStateAction<(State | null)[]>>,
    setAnimationState: Dispatch<SetStateAction<(State | null)[]>>,
    duration?: Duration,
    setRange: Dispatch<SetStateAction<RangeValue>>,
    timestampList: string[],
    setTimestampList: Dispatch<SetStateAction<string[]>>,
    realTimeLoadComplete: boolean,
    setRealTimeLoadComplete: Dispatch<SetStateAction<boolean>>,
}

const TimeSeriesContext = createContext<TimeSeriesContextValues>({
  play: () => {},
  stop: () => {},
  frameRating: '1',
  setFrameRating: () => {},
  range: [utcDayjs(), utcDayjs()],
  setRange: () => {},
  setCurrentPosition: () => {},
  currentPosition: utcDayjs().toISOString(),
  preloadRange: null,
  setPreloadRange: () => {},
  preloadingState: ['ready'],
  setPreloadingState: () => {},
  animationState: ['ready'],
  setAnimationState: () => {},
  duration: undefined,
  timestampList: [],
  setTimestampList: () => {},
  realTimeLoadComplete: false,
  setRealTimeLoadComplete: () => {},
});

const calcStates = (
  selectedProductsLength: number,
  activeProducts: number[],
) => Array(selectedProductsLength)
  .fill(null)
  .map((e, i) => {
    if (activeProducts.includes(i)) {
      return 'ready';
    }
    return e;
  });

function TimeSeriesProvider({ children } : TimeSeriesProviderProps) {
  const { message } = App.useApp();
  const { selectedProducts, activeProducts, capabilities } = useContext(ProductsContext);
  const { adsbIsEnabled } = useContext(MainAppContext);

  const states = useMemo(() => calcStates(
    selectedProducts.length,
    activeProducts,
  ), [activeProducts, selectedProducts.length]);

  const [range, setRange] = useState<RangeValue>([utcDayjs().clone().subtract(30, 'minute'), utcDayjs()]);
  const [frameRating, setFrameRating] = useState('1');
  const [currentPosition, setCurrentPosition] = useState<string>(utcDayjs().toISOString());
  const {
    setPlaybackMode, playbackMode, viewMode, realTimeMode,
  } = useContext(MainAppContext);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);
  const [preloadRange, setPreloadRange] = useState<RangeValue | null>([utcDayjs().clone().subtract(30, 'minute'), utcDayjs()]);
  const [
    preloadingState,
    setPreloadingState] = useState<(State | null)[]>(states);
  const [animationState, setAnimationState] = useState<(State | null)[]>(states);
  const [timestampList, setTimestampList] = useState<string[]>([]);
  const [realTimeLoadComplete, setRealTimeLoadComplete] = useState<boolean>(false);
  const currentPositionRef = useRef(currentPosition);

  const rangeStr = useMemo(() => {
    const layerInCapabilities = capabilities?.Capability
      .Layer
      .Layer
      .find((layer) => layer.Name === selectedProducts[0]?.internalName);

    return layerInCapabilities?.Dimension?.find((dimension) => dimension.name === Dimension.Time)?.values || `${range[0].toISOString()}/${range[1].toISOString()}/PT1M`;
  }, [capabilities?.Capability.Layer.Layer, range, selectedProducts]);
  const {
    duration,
  } = useMemo(() => calculateTime(rangeStr), [rangeStr]);

  const stop = useCallback(() => {
    if (timer.current) {
      clearInterval(timer.current);
    }
    setPlaybackMode(false);
  }, [setPlaybackMode]);

  const play = useCallback(() => {
    setPlaybackMode(true);
  }, [setPlaybackMode]);

  const values = useMemo(() => ({
    play,
    stop,
    frameRating,
    setFrameRating,
    range,
    setRange,
    setCurrentPosition,
    currentPosition,
    preloadRange,
    setPreloadRange,
    preloadingState,
    setPreloadingState,
    animationState,
    setAnimationState,
    duration,
    timestampList,
    setTimestampList,
    realTimeLoadComplete,
    setRealTimeLoadComplete,
  }), [
    currentPosition, frameRating, play, preloadRange,
    preloadingState, range, stop, duration, animationState,
    setAnimationState, timestampList, setTimestampList,
    realTimeLoadComplete, setRealTimeLoadComplete,
  ]);

  useEffect(() => {
    const initPosition = range?.[0] || utcDayjs();

    const { samplingDuration } = calculateRanges(range);

    let date = utcDayjs(currentPositionRef.current);
    let currentTimestampIndex = 0;

    // Find the current position in the timestampList
    if (timestampList.length > 0) {
      const currentTimeISO = currentPositionRef.current;
      currentTimestampIndex = timestampList.findIndex((timestamp) => timestamp === currentTimeISO);
      if (currentTimestampIndex === -1) {
        // If current position is not found, find the closest one or start from beginning
        currentTimestampIndex = 0;
      }
    }

    function run() {
      if (timer.current && timestampList.length > 0) {
        // Check if we've reached the end of the timestamp list
        if (currentTimestampIndex >= timestampList.length - 1) {
          // Reset to the beginning
          currentTimestampIndex = 0;
          setCurrentPosition(timestampList[0]);
          return;
        }

        // Move to the next timestamp
        currentTimestampIndex += 1;
        setCurrentPosition(timestampList[currentTimestampIndex]);
      } else if (timer.current && date?.isAfter(range?.[1])) {
        // Fallback to the original behavior if no timestampList is available
        setCurrentPosition(initPosition.toISOString());
        date = initPosition;
      } else if (!timestampList.length) {
        // Fallback to the original sampling duration approach
        const newTime = date?.clone().add(samplingDuration);
        if (newTime) {
          setCurrentPosition(newTime.toISOString());
          date = newTime;
        }
      }
    }
    if (playbackMode && animationState.some((state) => state === 'ready' || state === 'active')) {
      timer.current = setInterval(run, (1000 / parseInt(frameRating, 10)));
    }

    return () => {
      if (timer.current) {
        clearInterval(timer.current);
        timer.current = null;
      }
    };
  }, [animationState, duration, frameRating, message, playbackMode, range, timestampList]);

  useEffect(() => {
    if (currentPosition) {
      currentPositionRef.current = currentPosition;
    }
  }, [currentPosition]);

  useEffect(() => {
    setPreloadingState((prev) => prev.map((s, index) => (!s && activeProducts.includes(index) ? 'ready' : s)));
    setAnimationState((prev) => prev.map((s, index) => (!s && activeProducts.includes(index) ? 'ready' : s)));
  }, [activeProducts, selectedProducts.length, viewMode]);

  useEffect(() => {
    if (!activeProducts.length && playbackMode && !adsbIsEnabled) {
      stop();
    }
  }, [activeProducts.length, adsbIsEnabled, playbackMode, stop]);

  // Resetear el estado del servicio cuando cambie el modo
  useEffect(() => {
    if (realTimeMode) {
      enhancedImageService.resetState();
      setRealTimeLoadComplete(false);
    }
  }, [realTimeMode, setRealTimeLoadComplete]);

  // Mark when the first real-time load is completed
  useEffect(() => {
    if (realTimeMode && !realTimeLoadComplete) {
      // The state will be automatically updated in the service
      // when the first real-time image is completed
    }
  }, [realTimeMode, realTimeLoadComplete]);

  return (
    <TimeSeriesContext.Provider value={values}>
      {children}
    </TimeSeriesContext.Provider>
  );
}

export default TimeSeriesContext;
export { TimeSeriesProvider };
